import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { I18nService } from 'i18n';
import { CommunityService } from '../../services/community.service';
import {
  CommunityTranslationMode,
  CommunityTranslationReason,
  CommunityTranslationViewStatus,
  communitySameLocale
} from '../../models/community.models';
import {
  CommunityThreadTranslationService,
  CommunityTranslationEntry,
  CommunityTranslationKind
} from '../../services/community-thread-translation.service';
import { CommunityTranslationPreferenceService } from '../../services/community-translation-preference.service';
import { CommunityBodyPipe } from '../../pipes/community-body.pipe';
import { CommunityVideoEmbedsDirective } from '../community-video-embed/community-video-embeds.directive';

/**
 * Renders a thread's or a post's body and, when its source language differs from
 * the reader's, a compact source→target dropdown next to it (§3.3 of
 * docs/community/TRANSLATION.md): translate once, switch between translation and
 * original, and set what should happen with that language from now on.
 *
 * The state itself belongs to the page, not to this component: it injects
 * `CommunityThreadTranslationService` — provided by the thread detail page — so
 * one collective request covers the whole thread instead of one per post. The
 * injection is optional on purpose: wherever no page provides the coordinator
 * (and on backends without a provider) this still renders the body, and falls
 * back to the single-item endpoint for a one-off translation.
 */
@Component({
  selector: 'app-community-translate',
  standalone: true,
  imports: [CommonModule, TranslocoModule, CommunityBodyPipe, CommunityVideoEmbedsDirective],
  templateUrl: './community-translate.component.html',
  styleUrl: './community-translate.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityTranslateComponent {
  // Signal inputs on purpose: `offered()` and friends are `computed`, and a
  // computed only re-evaluates when a SIGNAL it read changes. With plain
  // `@Input`s the first evaluation — made while the overview was still loading
  // and `available` was false — was cached for good, and the dropdown never
  // appeared (2026-09-13).
  readonly kind = input.required<CommunityTranslationKind>();
  readonly targetId = input.required<number>();
  readonly originalBody = input<string>('');
  /** Threads only: shown in place of the heading while the translation is on screen. */
  readonly originalTitle = input<string | undefined>(undefined);
  /** The language the body was written in. Nothing is offered without it. */
  readonly sourceLocale = input.required<string>();
  /** Whether the network has a translation provider configured; hides every control when false. */
  readonly available = input<boolean>(false);

  private readonly coordinator = inject(CommunityThreadTranslationService, { optional: true });
  private readonly prefs = inject(CommunityTranslationPreferenceService);
  private readonly community = inject(CommunityService);
  private readonly i18n = inject(I18nService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly menuOpen = signal(false);
  /** Current UI language as a signal, so the badge and offer re-evaluate on a switch. */
  private readonly uiLocale = signal(this.i18n.getLanguage());
  /** State for pages that do not provide the coordinator (single-item endpoint). */
  private readonly standalone = signal<CommunityTranslationEntry | null>(null);

  readonly modes: CommunityTranslationMode[] = ['ask', 'always', 'never'];

  constructor() {
    this.i18n.currentLanguage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(lang => {
      if (!lang) return;
      if (!communitySameLocale(lang, this.uiLocale())) {
        this.uiLocale.set(lang);
        this.standalone.set(null);
      }
    });
  }

  /** The element's translation state, whoever is holding it. */
  readonly entry = computed<CommunityTranslationEntry | null>(() => {
    const fromPage = this.coordinator?.entries()[`${this.kind()}:${this.targetId()}`] ?? null;
    return fromPage ?? this.standalone();
  });

  readonly status = computed<CommunityTranslationViewStatus | null>(() => this.entry()?.status ?? null);
  readonly reason = computed<CommunityTranslationReason | null>(() => this.entry()?.reason ?? null);
  readonly showing = computed<boolean>(() => {
    const entry = this.entry();
    return !!entry && entry.status === 'ready' && entry.showing;
  });
  readonly hasTranslation = computed<boolean>(() => this.entry()?.status === 'ready');
  readonly pending = computed<boolean>(() => this.status() === 'pending');
  readonly failed = computed<boolean>(() => this.status() === 'failed' || this.status() === 'timeout');
  /** `too_long` cannot be retried — the source is simply above the model's limit. */
  readonly canRetry = computed<boolean>(() => this.failed() && this.reason() !== 'too_long');

  readonly failureKey = computed<string>(() => {
    if (this.status() === 'timeout') return 'community.translate.timeout';
    return this.reason() === 'too_long' ? 'community.translate.failed_too_long' : 'community.translate.failed';
  });

  /** The control is offered whenever the languages differ — `never` included, or it could not be undone. */
  readonly offered = computed<boolean>(() => this.prefs.shouldOffer(this.sourceLocale(), this.uiLocale(), this.available()));

  readonly mode = computed<CommunityTranslationMode>(() => this.prefs.modeFor(this.sourceLocale()));

  readonly sourceBadge = computed<string>(() => (this.sourceLocale() || '').toUpperCase());
  readonly targetBadge = computed<string>(() => this.uiLocale().toUpperCase());
  readonly sourceLanguageName = computed<string>(() => this.i18n.getLanguageName(this.sourceLocale()));

  get displayBody(): string {
    const entry = this.entry();
    return this.showing() && entry?.text ? entry.text : this.originalBody();
  }

  /**
   * The title to put in the heading: the translated one while the translation is
   * on screen, the original otherwise. Threads only — a post has no title.
   */
  get displayTitle(): string | null {
    const entry = this.entry();
    if (this.showing() && entry?.title) return entry.title;
    return this.originalTitle() ?? null;
  }

  // --- Menu ---------------------------------------------------------------------

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.menuOpen.update(open => !open);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen()) return;
    if (!this.host.nativeElement.contains(event.target as Node)) this.menuOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.menuOpen.set(false);
  }

  // --- Actions ------------------------------------------------------------------

  translateOnce(): void {
    this.menuOpen.set(false);
    if (this.coordinator) {
      this.coordinator.requestOne(this.kind(), this.targetId());
      return;
    }
    this.standaloneTranslate(false);
  }

  toggleShowing(): void {
    this.menuOpen.set(false);
    if (this.coordinator) {
      this.coordinator.toggleShowing(this.kind(), this.targetId());
      return;
    }
    const entry = this.standalone();
    if (entry?.status === 'ready') this.standalone.set({ ...entry, showing: !entry.showing });
  }

  retry(): void {
    if (this.coordinator) {
      this.coordinator.retry(this.kind(), this.targetId());
      return;
    }
    this.standaloneTranslate(true);
  }

  setMode(mode: CommunityTranslationMode): void {
    this.menuOpen.set(false);
    const sourceLocale = this.sourceLocale();
    if (!sourceLocale) return;
    this.prefs.setMode(sourceLocale, mode);
    if (this.coordinator) return; // the coordinator reacts to the change itself
    if (mode === 'never') {
      const entry = this.standalone();
      if (entry) this.standalone.set({ ...entry, showing: false });
    } else if (mode === 'always' && this.status() !== 'ready') {
      this.standaloneTranslate(false);
    }
  }

  /**
   * The pre-coordinator path: one request for this element only. Kept so the
   * component stays usable outside the thread page (search results, a future
   * list view) rather than silently losing its button there.
   */
  private standaloneTranslate(retry: boolean): void {
    const locale = this.uiLocale();
    const base: CommunityTranslationEntry = {
      kind: this.kind(),
      id: this.targetId(),
      sourceLocale: this.sourceLocale(),
      status: 'pending',
      showing: false,
      requested: true,
      pendingSince: Date.now()
    };
    this.standalone.set(base);
    const request = this.kind() === 'thread'
      ? this.community.translateThread(this.targetId(), locale)
      : this.community.translatePost(this.targetId(), locale);
    request.subscribe({
      next: res => {
        if (!communitySameLocale(locale, this.uiLocale())) return;
        if (res.available === false) {
          this.standalone.set({ ...base, status: 'unavailable', pendingSince: undefined });
          return;
        }
        const status = res.status ?? (res.text ? 'ready' : 'failed');
        if (status === 'ready') {
          this.standalone.set({
            ...base,
            status: 'ready',
            title: res.title,
            text: res.text,
            showing: true,
            pendingSince: undefined
          });
          return;
        }
        if (status === 'pending' && !retry) {
          // No polling loop outside the thread page; ask once more on the next click.
          this.standalone.set({ ...base, status: 'pending' });
          return;
        }
        this.standalone.set({ ...base, status, reason: res.reason, pendingSince: undefined });
      },
      error: () => this.standalone.set({ ...base, status: 'failed', reason: 'error', pendingSince: undefined })
    });
  }
}
